import InfoIcon from '@mui/icons-material/Info';
import { Box, Grid, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  getTotalMailingCreditsAllotted,
  getMassMailingCredsPerDay,
  IAddCreditsPayload,
  addCreditsToWorkspaceUser,
  deleteAssignedCredsToWorkspaceUser,
  getDefinedCreditsForWorkspaceUsers,
  resetToDefaultMailingsCredits,
  usersAdvanceFilterQuery,
} from 'wisestep-api';
import {
  ControlledTextInput,
  Label,
  WsAutoComplete,
  WsButton,
  WsChip,
  WsChipVariants,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { HeaderBox } from '../../jobSettings/components/HeaderBox';
import WorkspaceUserCredits from '../components/WorkspaceUserCredits';

const noteForUser =
  'NOTE: If any un-allocated credits left without sharing then you cannot use those credits like you use in default workspace credits usage flow. Either allot all remaining credits to users or switch to default workspace credits usage flow.';

const BoldLabel = styled(Label)(() => ({
  fontWeight: 'bold',
}));
type creditsDataType = {
  totalCredits: number;
  usedCredits: number;
};
const ShareCreditsPage = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm();
  const { handleSubmit } = methods;
  const [creditsData, setCreditsData] = useState<creditsDataType>({
    totalCredits: 0,
    usedCredits: 0,
  });
  const [suggestionsData, setSuggestionData] = useState<string[]>([]);
  const [allocatedUsersData, setAllocatedUsersData] = useState({});
  const [sEmail, setSEmail] = useState<string>('');
  useEffect(() => {
    (async function () {
      const totalCredsData = await getMassMailingCredsPerDay();
      const totalAllocatedCreds = await getTotalMailingCreditsAllotted();
      const getDefinedCreditsForWorkspaceUsersData = await getDefinedCreditsForWorkspaceUsers();
      if (totalCredsData.data && totalAllocatedCreds.data)
        setCreditsData({
          totalCredits: totalCredsData.data[0].balance,
          usedCredits: totalAllocatedCreds.data.totalAllottedInWorkspace,
        });

      setAllocatedUsersData(getDefinedCreditsForWorkspaceUsersData.data);
    })();
  }, []);

  const workspaceRecruiterSearchAPI = async (query: string) => {
    if (query.length >= 2) {
      const tempArray: string[] = [];
      const workspaceRecruiterSuggestions = await usersAdvanceFilterQuery(query);
      workspaceRecruiterSuggestions.data.map((data) => tempArray.push(data?.userId));
      setSuggestionData(tempArray);
    }
  };
  const onEmailSelect = (selectedEmail: string[]) => setSEmail(selectedEmail[0]);

  const saveToAPI = async (data: FieldValues) => {
    if (data.credits && sEmail)
      try {
        const payload: IAddCreditsPayload = {
          creditsPerDay: data.credits,
          email: sEmail,
          selectedSuggestion: [
            {
              id: uuidv4(),
              text: sEmail,
            },
          ],
        };
        const sharedCredsUsers = await addCreditsToWorkspaceUser(payload);
        if (sharedCredsUsers.data) setAllocatedUsersData(sharedCredsUsers.data);
        fetchLatestData();
        setNotification(success('Credits Shared successfully'));
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
  };
  const onEditAPiHandler = async (editData, key) => {
    try {
      const payload: IAddCreditsPayload = {
        creditsPerDay: editData,
        email: key,
      };
      const sharedCredsUsers = await addCreditsToWorkspaceUser(payload);
      if (sharedCredsUsers.data) setAllocatedUsersData(sharedCredsUsers.data);
      setNotification(success('Credits Updated successfully'));
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };
  const fetchLatestData = async () => {
    try {
      const getDefinedCreditsForWorkspaceUsersData = await getDefinedCreditsForWorkspaceUsers();
      const totalCredsData = await getMassMailingCredsPerDay();
      const totalAllocatedCreds = await getTotalMailingCreditsAllotted();
      if (totalCredsData.data && totalAllocatedCreds.data)
        setCreditsData({
          totalCredits: totalCredsData.data[0].balance,
          usedCredits: totalAllocatedCreds.data.totalAllottedInWorkspace,
        });
      setAllocatedUsersData(getDefinedCreditsForWorkspaceUsersData.data);
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };
  const onDeleteAPIHandler = async (key) => {
    try {
      await deleteAssignedCredsToWorkspaceUser(key);
      setNotification(success('Credits Reverted successfully'));
      fetchLatestData();
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };
  const onResetAPIHandler = async () => {
    try {
      await resetToDefaultMailingsCredits();
      setNotification(success('Credits Reverted successfully'));
      fetchLatestData();
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };
  return (
    <SettingsPageLayout
      title={'Mass Mailing credits'}
      indexOptions={[{ label: 'Share Credits', url: '/vendors/mailing-credits' }]}
      backUrl={'/app-settings/apps-and-integrations'}
    >
      <FormProvider {...methods}>
        <Box px={8} pt={2}>
          <HeaderBox mb={4}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE}>
              Mailing Credits Sharing To Workspace Users
            </WsTypography>
          </HeaderBox>

          <Grid container xs={12} direction="column" mb={5}>
            <Grid item>
              <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.PRIMARY}>
                {noteForUser}
              </WsTypography>
            </Grid>
            <Grid item display="flex" direction="row" mt={1}>
              <Label sx={{ fontWeight: 'bold' }}>Credits Un-allotted/Total </Label>
              <Tooltip title="Credits un-alloted against to the total credits defined for workspace">
                <InfoIcon sx={{ mx: 1, mt: 0.5 }} />
              </Tooltip>
              <WsChip
                value={`${creditsData?.totalCredits - creditsData?.usedCredits}/${
                  creditsData.totalCredits
                }`}
                variant={WsChipVariants.PRIMARY}
              />
            </Grid>
            <Grid item display="flex" direction="row" mt={1}>
              <Label sx={{ fontWeight: 'bold' }}>
                Click
                {
                  <WsButton onClick={() => onResetAPIHandler()} variant="text">
                    Reset
                  </WsButton>
                }
                to Default to switch back to default credits usage flow.
              </Label>
            </Grid>
          </Grid>

          <Grid container xs={12} mb={4}>
            <Grid item xs={12} mb={2}>
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                Email Credits
              </WsTypography>
            </Grid>

            <Grid item xs={5} pr={2}>
              <BoldLabel sx={{ mb: 0.5 }}>Share with</BoldLabel>
              <WsAutoComplete
                onValueEnter={workspaceRecruiterSearchAPI}
                onTagsChange={onEmailSelect}
                placeholder="Johnson"
                initialValue={[]}
                optionsInStringArray={suggestionsData}
                multiple={false}
                freeSolo={false}
                name="workspaceRecruiterSearch"
                size="small"
              />
            </Grid>

            <Grid item xs={4}>
              <ControlledTextInput
                name="credits"
                labelText="Mailing Credits/Day*:"
                type="number"
                fullWidth
                id="credits"
                placeholder="credits"
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={3} display={'flex'} justifyContent={'center'} pt={'2rem'}>
              <WsButton
                type="submit"
                onClick={handleSubmit(saveToAPI)}
                variant="outlined"
                size="large"
              >
                Share Credits
              </WsButton>
            </Grid>
          </Grid>

          <Grid container xs={12}>
            <Grid item container xs={12} mb={3}>
              <Grid item xs={5} pr={2}>
                <BoldLabel>Workspace User</BoldLabel>
              </Grid>
              <Grid item xs={4}>
                <BoldLabel>Mailing Credits/Day</BoldLabel>
              </Grid>
            </Grid>
            <WorkspaceUserCredits
              allocatedData={allocatedUsersData}
              onSaveHandler={(data, key) => onEditAPiHandler(data, key)}
              onDeleteHandler={(key) => onDeleteAPIHandler(key)}
            />
          </Grid>
        </Box>
      </FormProvider>
    </SettingsPageLayout>
  );
};

export default ShareCreditsPage;
